/**
* Webmail Client Application entry point
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
import {createApp}                 from 'vue'
import Webmail                     from '@/vue/WebmailApp.vue'
import router                      from '@/vue/router';
import FocusDirective              from '@/vue/Directives/FocusDirective';
import TooltipDirective            from '@/vue/Directives/TooltipDirective.js';
import ContextMenuDirective        from '@/vue/Directives/ContextMenuDirective.js';
import ContextClickDirective       from '@/vue/Directives/ContextClickDirective.js';
import DragStartDirective          from '@/vue/Directives/DragStartDirective.js';
import DragDropDirective           from '@/vue/Directives/DragDropDirective.js';
import WindowDragDropDirective     from '@/vue/Directives/WindowDragDropDirective.js';
import SmartRenderScrollDirective  from '@/vue/Directives/SmartRenderScrollDirective.js';
import ScrollToElementDirective    from '@/vue/Directives/ScrollToElementDirective.js';
import FoldersController           from '@/js/Mailboxes/FoldersController.js';
import IdentitiesController        from '@/js/Settings/Identities/IdentitiesController.js';
import MessagesOptionsController   from '@/js/Settings/MessagesOptions/MessagesOptionsController.js';
import DomController               from '@/js/Webmail/DomController.js';
import AccountController           from '@/js/Webmail/AccountController.js';
import PreLoaderController         from '@/js/Webmail/PreLoaderController.js';
import ContactsController          from '@/js/Contacts/ContactsController.js';
import HTTPService                 from "@/js/Shared/Services/HTTP/HTTPService";
import StringPrototype             from '@/js/Prototypes/StringPrototype';
import ClientLoggingController     from '@/js/Webmail/ClientLogging/ClientLoggingController.js';
import LayoutSettingsController    from '@/js/Webmail/LayoutSettingsController';
import PopupController             from '@/js/Webmail/PopupController.js';
import PushNotificationsController from '@/js/Webmail/WebSockets/PushNotificationsController';
import SAPOMailConfig              from './SAPOMailConfig';
import BindSession                     from '@/js/Webmail/BindSession.js';
import GPTAdsController            from '@/js/Webmail/Ads/GPTAdsController';

class SapoMail {
  /**
   * Vue APP
   *
   * @private
   *
   * @param {Object}
   *
   * @since 7.0.0
   */
  app;

  /**
   * Executes SAPO Mail Application
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  async execute() {
    if (PopupController.isValidPopup() === true) {
      this.loadPopupApp();
      return;
    }

    await this.loadFullApp();
  }

  async loadFullApp() {
    DomController.load();
    PreLoaderController.load();
    PreLoaderController.start();

    if (await BindSession.call() === false) {
      BindSession.redirectOutside();
      return;
    }

    if (await this.loadServerConfig() === false) {
      return;
    }

    if (BindSession.refreshValidity() === false) {
      BindSession.redirectOutside();
      return;
    }

    this.setContent();
    this.fetchContent();
    this.setApp();
    this.initializeGPTAds();
    this.configureApp();
    this.loggingJavascript();
  }

  async loadPopupApp() {
    DomController.load();

    if (await this.loadServerConfig() === false) {
      return;
    }

    if (await BindSession.call() === false) {
      BindSession.redirectOutside();
      return;
    }

    this.setContent();
    this.setApp();
    this.configureApp();
    this.loggingJavascript();
  }

  async loadServerConfig() {
    let response = await HTTPService.post('webmail.php', null, null, true, false);

    if (response.success === false) {
      window.location = (response.results && response.results.redirect) ? response.results.redirect : '/';
      return false;
    }

    if (!response.results || !response.results.server) {
      return false;
    }

    SAPOMailConfig.server = response.results.server;

    return true;
  }

  /**
   * Creates Vue App based on the hash params
   *
   * @private
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  setApp() {
    this.app = createApp(Webmail);
  }

  /**
   * Configures the vue app
   *
   * @private
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  configureApp() {
    this.app.directive('focus'              , FocusDirective);
    this.app.directive('tooltip'            , TooltipDirective);
    this.app.directive('context-menu'       , ContextMenuDirective);
    this.app.directive('drag-start'         , DragStartDirective);
    this.app.directive('drag-drop'          , DragDropDirective);
    this.app.directive('window-drag-drop'   , WindowDragDropDirective);
    this.app.directive('context-click'      , ContextClickDirective);
    this.app.directive('scroll-to-element'  , ScrollToElementDirective)
    this.app.directive('smart-render-scroll', SmartRenderScrollDirective)
    this.app.use(router).mount('#webmail');
  }

  /**
   * Sets or loads content for the application in the respective controllers
   *
   * @private
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  setContent() {
    AccountController.mapAccount();
    IdentitiesController.setIdentities(AccountController.getIdentities());
    MessagesOptionsController.setOptions(AccountController.getMessageOptions());
    LayoutSettingsController.loadStorage();
    LayoutSettingsController.setDomSettings();
    AccountController.addNotifications();
  }

  /**
   * Fetches content for the application in the respective controllers
   *
   * @private
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  fetchContent() {
    FoldersController.fetchMain();
    ContactsController.fetchContactsOnce();
  }

  /**
   * Collects errors that occur in the app and sends
   * to the server
   *
   * @private
   *
   * @since 7.0.0
   *
   * @returns {void}
   */
  loggingJavascript(){
    if(!SAPOMailConfig.server.logging.enabled){
      return;
    }

    this.app.config.errorHandler = ClientLoggingController.onVueError.bind(ClientLoggingController);
    this.app.config.warnHandler = ClientLoggingController.onVueWarn.bind(ClientLoggingController);
  }

  initializeGPTAds() {
      if (AccountController.account.premium.enabled) {
          return;
      }
      
      GPTAdsController.startGPT();
  }
}

if (window.SUPPORTS_MAIL === true) {
  const mail = new SapoMail();
  mail.execute();
}
